import { NgModule } from '@angular/core';
import { RouterModule, Routes, Router } from '@angular/router';

// Home / Quote / Contact / Privacy / Service / Signout / Signinfo2 — loadComponent
// Company / Passenger / Billing / Transporter / Journey / Subscription / etc. — lazy
import { aclGuard } from './acl.guard';
import { AuthGuard, authGuard } from './auth.guard';
import { HybridAuthGuard, hybridAuthGuard } from './hybrid-auth.guard';
import { PatientIntakeRequiredGuard, patientIntakeRequiredGuard } from './patient-intake-required.guard';
import { SignupIncompleteGuard, signupIncompleteGuard } from './signup-incomplete.guard';
import { AssistantAppGuard, assistantAppGuard } from './assistant-app.guard';
import { content } from 'src/environments/data';
import { webddn } from 'src/environments/webddn';
import { CONTENT_ROUTES } from './content/content.routes';
import { FEATURES } from '../optional-components';
import { DynamicRouteService } from './services/dynamic-route.service';
import { storefrontHasProductsGuard } from './services/storefront-has-products.guard';

const loadHome = () => import('./home/home.component').then((m) => m.HomeComponent);

// Define core routes for the application
const coreRoutes: Routes = [
  {
    path: '',
    loadComponent: loadHome,
    title: content.WEBDDN_TITTLE,
    data: { meta: content.WEBDDN_META },
    canActivate: [hybridAuthGuard, patientIntakeRequiredGuard, signupIncompleteGuard],
  },
  {
    path: content.WEBDDN_PRODUCT_PATH,
    loadChildren: () => import('./journey/journey.module').then(m => m.JourneyModule),
    canActivate: [authGuard, patientIntakeRequiredGuard, signupIncompleteGuard],
    data: { meta: content.WEBDDN_META, breadcrumb:content.WEBDDN_PRODUCTS }
  },
  {
    path: content.WEBDDN_JOURNEY,
    loadChildren: () => import('./journey/journey.module').then(m => m.JourneyModule),
    canActivate: [authGuard, patientIntakeRequiredGuard, signupIncompleteGuard],
    data: { meta: content.WEBDDN_META, breadcrumb: content.WEBDDN_JOURNEYS }
  },
  {
    path: content.WEBDDN_TRANSPORTER,
    loadComponent: () =>
      import('./transporter/transporter.component').then((m) => m.TransporterComponent),
    canActivate: [aclGuard],
    data: { requiredPermission: 'feature.transporter', meta: content.WEBDDN_META, breadcrumb: content.WEBDDN_TRANSPORTER },
  },
  {
    path: content.WEBDDN_BUSINESS_USER,
    loadComponent: () =>
      import('./business-user/business-user.component').then((m) => m.BusinessUserComponent),
    canActivate: [aclGuard],
    data: { requiredPermission: 'feature.business_user', meta: content.WEBDDN_META, breadcrumb: content.WEBDDN_BUSINESS_USER },
  },
  {
    path: 'subscription',
    loadChildren: () =>
      import('./subscription/subscription.routes').then((m) => m.SUBSCRIPTION_ROUTES),
  },
  {
    path: content.WEBDDN_PASSENGER,
    loadChildren: () => import('./passenger/passenger.module').then(m => m.PassengerModule),
    canActivate: [authGuard, patientIntakeRequiredGuard, signupIncompleteGuard, aclGuard],
    data: { requiredPermission: 'feature.passenger', meta: content.WEBDDN_META, breadcrumb: content.WEBDDN_PASSENGERS }
  },
  {
    path: content.WEBDDN_COMPANY,
    loadChildren: () => import('./company/company.module').then(m => m.CompanyModule),
    canActivate: [authGuard, signupIncompleteGuard, aclGuard],
    data: { requiredPermission: 'feature.company', meta: content.WEBDDN_META, breadcrumb: 'Companies' }
  },
  {
    path: 'invoice-management',
    loadChildren: () => import('./invoice-management/invoice-management.module').then(m => m.InvoiceManagementModule),
    canActivate: [authGuard, signupIncompleteGuard, aclGuard],
    canActivateChild: [aclGuard],
    data: { requiredPermission: 'feature.invoice', meta: content.WEBDDN_META, breadcrumb: 'Invoice Management' }
  },
  {
    path: 'billings',
    loadChildren: () => import('./billing/billing.module').then(m => m.BillingModule),
    canActivate: [authGuard, signupIncompleteGuard],
    // Child routes declare their own aclGuard + requiredPermission (no parent gate).
    data: { meta: content.WEBDDN_META, breadcrumb: 'Billings' }
  },
  {
    path: 'admin',
    loadChildren: () => import('./admin/admin.module').then(m => m.AdminModule),
    canActivate: [authGuard, signupIncompleteGuard],
    data: { meta: content.WEBDDN_META, breadcrumb: 'Admin' }
  },
  {
    path: 'apikeys',
    loadChildren: () => import('./apikeys/apikeys.module').then(m => m.ApikeysModule),
    canActivate: [aclGuard],
    data: { requiredPermission: 'feature.apikeys', meta: content.WEBDDN_META, breadcrumb: 'API Keys Management' }
  },
  {
    path: 'registration',
    loadChildren: () => import('./registration/registration.module').then(m => m.RegistrationModule),
    canActivate: [aclGuard],
    data: { requiredPermission: 'feature.registration', meta: content.WEBDDN_META, breadcrumb: 'Registration Management' }
  },
  {
    path: 'roster',
    loadChildren: () => import('./roster/roster.module').then(m => m.RosterModule),
    canActivate: [aclGuard],
    data: { requiredPermission: 'feature.roster', meta: content.WEBDDN_META, breadcrumb: 'Roster Management' }
  },
  {
    path: 'fwdu',
    loadChildren: () => import('./fwdu/fwdu.module').then(m => m.FwduModule),
    canActivate: [aclGuard],
    data: { requiredPermission: 'feature.fwdu', meta: content.WEBDDN_META, breadcrumb: 'Fwdu' }
  },
  {
    path: 'flat-rate',
    loadComponent: () =>
      import('./flat-rate/flat-rate.component').then((m) => m.FlatRateComponent),
    canActivate: [aclGuard],
    data: { requiredPermission: 'feature.flat_rate', meta: content.WEBDDN_META, breadcrumb: 'Flat Rate Management' },
  },
  {
    path: 'distance-matrix-cache',
    loadChildren: () =>
      import('./distance-matrix-cache/distance-matrix-cache.module').then(
        (m) => m.DistanceMatrixCacheModule
      ),
    canActivate: [aclGuard],
    data: { requiredPermission: 'feature.distance_matrix', meta: content.WEBDDN_META, breadcrumb: 'Distance Matrix Cache' },
  },
  {
    path: 'sign-project',
    loadChildren: () => import('./sign-project/sign-project.module').then(m => m.SignProjectModule),
    canActivate: [aclGuard],
    data: { requiredPermission: 'feature.sign_project', meta: content.WEBDDN_META, breadcrumb: 'Sign Projects' }
  },
  {
    path: 'font-management',
    loadChildren: () => import('./font-management/font-management.module').then(m => m.FontManagementModule),
    canActivate: [aclGuard],
    data: { requiredPermission: 'feature.font', meta: content.WEBDDN_META, breadcrumb: 'Font Management' }
  },
  {
    path: 'tournament',
    loadChildren: () => import('./tournament/tournament.module').then(m => m.TournamentModule),
    // Public registration stays ungated; admin tournament child routes use aclGuard + sport.tournament
    data: { meta: content.WEBDDN_META, breadcrumb: 'Tournament Management' }
  },
  {
    path: 'webddn-project',
    loadChildren: () => import('./webddn-project/webddn-project.module').then(m => m.WebddnProjectModule),
    canActivate: [aclGuard],
    data: { requiredPermission: 'feature.webddn_project', meta: content.WEBDDN_META, breadcrumb: 'WebDDN Project Management' }
  },
  {
   path: 'design-system',
   loadComponent: () => import('./design-system/design-system.component').then(m => m.DesignSystemComponent),
   canActivate: [aclGuard],
   data: { requiredPermission: 'feature.design_system', meta: content.WEBDDN_META, breadcrumb: 'Design System' }
  },
  {
    path: 'privacy',
    loadComponent: () => import('./privacy/privacy.component').then((m) => m.PrivacyComponent),
    data: { meta: content.WEBDDN_META, breadcrumb: 'Privacy Policy' },
  },
  {
    path: 'service',
    loadComponent: () => import('./service/service.component').then((m) => m.ServiceComponent),
    data: { meta: content.WEBDDN_META, breadcrumb: 'Service' },
  },
  {
    path: 'terms',
    loadComponent: () => import('./service/service.component').then((m) => m.ServiceComponent),
    data: { meta: content.WEBDDN_META, breadcrumb: 'Terms of Service' },
  },
  {
    path: 'contact',
    loadComponent: () => import('./contact/contact.component').then((m) => m.ContactComponent),
    data: { meta: content.WEBDDN_META, breadcrumb: 'Contact' },
  },
  {
    path: 'product-list',
    loadComponent: () =>
      import('./product-list/product-list.component').then((m) => m.ProductListComponent),
    data: { meta: content.WEBDDN_META, breadcrumb: 'Product List' },
  },
  {
    path: 'unauthorized',
    loadComponent: loadHome,
    data: { isUnauthorized: true, meta: content.WEBDDN_META, breadcrumb: 'Unauthorized' }
  },
  {
    path: 'product-management',
    loadChildren: () => import('./product-management/product-management.module').then(m => m.ProductManagementModule),
    canActivate: [authGuard, aclGuard],
    data: { requiredPermission: 'feature.product', meta: content.WEBDDN_META, breadcrumb: 'Product Management' }
  },
  {
    path: 'shop',
    loadChildren: () => import('./storefront/storefront.module').then(m => m.StorefrontModule),
    canActivate: [storefrontHasProductsGuard],
    data: { meta: content.WEBDDN_META, breadcrumb: 'Shop' }
  },
  {
    path: 'paymentform',
    loadComponent: () =>
      import('./payment-form/payment-form.component').then((m) => m.PaymentFormComponent),
    data: { meta: content.WEBDDN_META, breadcrumb: 'Payment Form' },
  },
  {
    path: 'payment-confirmation',
    loadComponent: () =>
      import('./payment-confirmation/payment-confirmation.component').then(
        (m) => m.PaymentConfirmationComponent
      ),
    data: { meta: content.WEBDDN_META, breadcrumb: 'Payment Confirmation' },
  },
  {
    path: content.WEBDDN_SIGNUP_PATH,
    loadChildren: () => import('./signup/signup.module').then(m => m.SignupModule),
    data: { meta: content.WEBDDN_META, breadcrumb: content.WEBDDN_SIGNUP }
  },
  {
    path: content.WEBDDN_LOGIN_PATH,
    loadComponent: () => import('./login/login.component').then((m) => m.LoginComponent),
    data: { meta: content.WEBDDN_META, breadcrumb: content.WEBDDN_LOGIN },
  },
  {
    path: 'forgot-password',
    loadComponent: () =>
      import('./forgot-password/forgot-password.component').then((m) => m.ForgotPasswordComponent),
    data: { meta: content.WEBDDN_META, breadcrumb: 'Forgot Password' },
  },
  {
    path: 'reset-password/:token',
    loadComponent: () =>
      import('./reset-password/reset-password.component').then((m) => m.ResetPasswordComponent),
    data: { meta: content.WEBDDN_META, breadcrumb: 'Reset Password' },
  },
  {
    path: 'profile',
    loadComponent: () => import('./user/user.component').then((m) => m.UserComponent),
    canActivate: [authGuard, patientIntakeRequiredGuard, signupIncompleteGuard],
    data: { meta: content.WEBDDN_META, breadcrumb: 'My Profile' },
  },
  { path: 'notifications', loadComponent: () => import('./notifications/notifications-inbox.component').then(m => m.NotificationsInboxComponent), canActivate: [authGuard, patientIntakeRequiredGuard, signupIncompleteGuard, aclGuard], data: { requiredPermission: 'feature.notifications', meta: content.WEBDDN_META, breadcrumb: 'Notifications'} },
  { path: 'my-schedule', loadComponent: () => import('./my-schedule/my-schedule.component').then(m => m.MyScheduleComponent), canActivate: [authGuard, patientIntakeRequiredGuard, signupIncompleteGuard, aclGuard], data: { requiredPermission: 'feature.my_schedule', meta: content.WEBDDN_META, breadcrumb: 'My Schedule'} },
  {
    path: 'autocomplete',
    loadComponent: () =>
      import('./autocomplete/autocomplete.component').then((m) => m.AutocompleteComponent),
    data: { meta: content.WEBDDN_META, breadcrumb: 'Autocomplete' },
  },
  {
    path: 'quote',
    loadComponent: () => import('./quote/quote.component').then((m) => m.QuoteComponent),
    data: { meta: content.WEBDDN_META, breadcrumb: 'Quote' },
  },
  {
    path: 'signout',
    loadComponent: () => import('./signout/signout.component').then((m) => m.SignoutComponent),
    data: { meta: content.WEBDDN_META, breadcrumb: 'Sign Out' },
  },
  {
    path: 'checkout',
    loadComponent: () =>
      import('./checkout/checkout.component').then((m) => m.CheckoutComponent),
    data: { meta: content.WEBDDN_META, breadcrumb: 'Checkout' },
  },
  {
    path: 'success',
    loadComponent: () => import('./success/success.component').then((m) => m.SuccessComponent),
    data: { meta: content.WEBDDN_META, breadcrumb: 'Success' },
  },
  {
    path: 'success/:orderId',
    loadComponent: () => import('./success/success.component').then((m) => m.SuccessComponent),
    data: { meta: content.WEBDDN_META, breadcrumb: 'Order Success' },
  },
  {
    path: content.WEBDDN_KANBAN_PATH,
    loadChildren: () => import('./kanban/kanban.routes').then((m) => m.KANBAN_ROUTES),
    canActivate: [authGuard, aclGuard],
    data: { requiredPermission: 'feature.kanban', meta: content.WEBDDN_META, breadcrumb: content.WEBDDN_KANBAN },
  },
  {
    path: 'blackboard',
    loadChildren: () =>
      import('./blackboard/blackboard.routes').then((m) => m.BLACKBOARD_ROUTES),
    canActivate: [authGuard, aclGuard],
    data: {
      requiredPermission: 'feature.blackboard',
      meta: content.WEBDDN_META,
      breadcrumb: 'Blackboard',
    },
  },
  {
    path: 'thankyou',
    loadComponent: () => import('./thankyou/thankyou.component').then((m) => m.ThankyouComponent),
    data: { meta: content.WEBDDN_META, breadcrumb: 'Thank You' },
  },
  {
    path: 'signinfo2',
    loadComponent: () => import('./signinfo2/signinfo2.component').then((m) => m.Signinfo2Component),
    data: { meta: content.WEBDDN_META, breadcrumb: 'Sign Info 2' },
  },
  ...CONTENT_ROUTES,
  {
    path: 'scraper',
    loadComponent: () => import('./scraper/scraper.component').then((m) => m.ScraperComponent),
    canActivate: [aclGuard],
    data: { requiredPermission: 'feature.scraper', meta: content.WEBDDN_META, breadcrumb: 'Scrapper' },
  },
  {
    path: 'google-photos-callback',
    loadComponent: () =>
      import('./google-photos-callback/google-photos-callback.component').then(
        (m) => m.GooglePhotosCallbackComponent
      ),
    data: { meta: content.WEBDDN_META, breadcrumb: 'Google Photos' },
  },
  // Customer approval route (no authentication required)
  {
    path: 'customer-approval/:token',
    loadComponent: () =>
      import('./sign-project/customer-approval/customer-approval.component').then(
        (m) => m.CustomerApprovalComponent
      ),
    data: { meta: content.WEBDDN_META, breadcrumb: 'Customer Approval' },
  },
  // Pain Assessment Module (Lazy loaded)
  {
    path: 'patient-assessment',
    loadChildren: () => import('./pain-assessment/pain-assessment.module').then(m => m.PainAssessmentModule),
    canActivate: [aclGuard],
    data: { requiredPermission: 'feature.patient_assessment', meta: content.WEBDDN_META, breadcrumb: 'Patient Assessment' }
  },
  // Patient Intake Module (Lazy loaded)
  {
    path: 'patient-intake',
    loadChildren: () => import('./patient-intake/patient-intake.module').then(m => m.PatientIntakeModule),
    canActivate: [aclGuard],
    data: { requiredPermission: 'feature.patient_intake', meta: content.WEBDDN_META, breadcrumb: 'Patient Intake' }
  },
  // Book Appointment Module (public - no auth required)
  {
    path: 'book-appointment',
    loadChildren: () => import('./book-appointment/book-appointment.module').then(m => m.BookAppointmentModule),
    data: { meta: content.WEBDDN_META, breadcrumb: 'Book Appointment' }
  },
  {
    path: 'health-fitness',
    loadChildren: () => import('./health-fitness/health-fitness.module').then(m => m.HealthFitnessModule),
    canActivate: [authGuard, assistantAppGuard, aclGuard],
    canActivateChild: [aclGuard],
    data: { requiredPermission: 'feature.health_fitness', meta: content.WEBDDN_META, breadcrumb: 'Health & Fitness' }
  },
  {
    path: 'finance-life',
    loadChildren: () => import('./finance-life/finance-life.module').then(m => m.FinanceLifeModule),
    canActivate: [authGuard, assistantAppGuard, aclGuard],
    data: { requiredPermission: 'feature.finance_life', meta: content.WEBDDN_META, breadcrumb: 'Finance & Life' }
  },
  // Add redirect for common typos or old paths
  { path: 'oldPath', redirectTo: '/staticPath' }
];

// Get optional team route - only add if both properties exist
const getTeamRoute = (): Routes => {
  const teamPath = (content as any).WEBDDN_TEAM_PATH;
  const teamBreadcrumb = (content as any).WEBDDN_TEAM;
  if (teamPath && teamBreadcrumb) {
    return [{
      path: teamPath,
      loadChildren: () => import('./team/team.module').then(m => m.TeamModule),
      data: { meta: content.WEBDDN_META, breadcrumb: teamBreadcrumb }
    }];
  }
  return [];
};

// Get optional feature routes based on feature flags
const getFeatureRoutes = (): Routes => {
  const featureRoutes: Routes = [];
  if (FEATURES['publish']?.enabled) {
    featureRoutes.push({
      path: 'publish',
      loadComponent: () => import('./publish/publish.component').then(m => m.PublishComponent),
      data: { meta: content.WEBDDN_META, breadcrumb: 'Publish' }
    });
  }
  if (FEATURES['dashboard']?.enabled) {
    featureRoutes.push({
      path: 'dashboard',
      loadComponent: () => import('./webddn/dashboard/dashboard.component').then(m => m.DashboardComponent),
      data: { meta: content.WEBDDN_META, breadcrumb: 'Dashboard' }
    });
  }
  if (FEATURES['cloudflare']?.enabled) {
    featureRoutes.push({
      path: 'cloudflare',
      loadComponent: () => import('./cloudflare-cache/cloudflare-cache.component').then(m => m.CloudflareCacheComponent),
      canActivate: [aclGuard],
      data: { requiredPermission: 'feature.cloudflare', meta: content.WEBDDN_META, breadcrumb: 'Cloudflare Cache' }
    });
  }
  return featureRoutes;
};

// Combine all routes
const routes: Routes = [
  ...coreRoutes,
  ...getTeamRoute(),  // Add conditional team route
  ...getFeatureRoutes(),
  // Android WebView loads .../assets/index.html#/ — PathLocationStrategy sees index.html
  { path: 'index.html', redirectTo: '', pathMatch: 'full' },
  { path: 'index.dev.html', redirectTo: '', pathMatch: 'full' },
  // Important: Wildcard route must be the last route in the configuration
  // to catch all unmatched URLs
  { path: '**', loadComponent: loadHome, data: { meta: content.WEBDDN_META, isNotFound: true } }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { 
    useHash: webddn.USEHASH, 
    onSameUrlNavigation: 'ignore',
    // Add URL normalization to handle URL encoding issues
    urlUpdateStrategy: 'eager',
    paramsInheritanceStrategy: 'always'
  })],
  exports: [RouterModule]
})
export class AppRoutingModule {
  constructor(private router: Router, private dynamicRouteService: DynamicRouteService) {
    // Dynamic routes will be added by the service
    // The service will preserve the wildcard route at the end
  }
}
